import { useAuthStore } from "@/modules/auth/store";
import { computed } from "vue";

/**
 * Composable для проверки прав доступа пользователя
 * Permissions используются в формате - сущность:действие, напр. application:index
 * index - просмотр списка
 * show - просмотр одного
 * store - создание
 * update - редактирование
 * destroy - удаление
 * @returns {Object} Методы для проверки прав
 */
export const usePermissions = () => {
  const authStore = useAuthStore();

  const permissions = computed(() => {
    const roles = authStore.user.roles || [];
    return roles.flatMap((role) => role.permissions?.map((p) => p.name) || []);
  });

  /**
   * Проверка наличия конкретного права у пользователя
   * @param {string} permission - Название права (например, 'counterparties.create')
   * @returns {boolean}
   */
  const hasPermission = (permission) => {
    if (!authStore.user) return false;

    return permissions.value.includes(permission) || false;
  };

  /**
   * Проверка наличия хотя бы одного права из списка
   * @param {Array<string>} permissions - Массив названий прав
   * @returns {boolean}
   */
  const hasAnyPermission = (permissions = []) => {
    return permissions.some((permission) => hasPermission(permission));
  };

  /**
   * Проверка наличия всех прав из списка
   * @param {Array<string>} permissions - Массив названий прав
   * @returns {boolean}
   */
  const hasAllPermissions = (permissions = []) => {
    return permissions.every((permission) => hasPermission(permission));
  };

  /**
   * Проверка наличия доступов
   * @param {Array<string>} permissions - Массив названий прав, или строка
   * @param check - any - hasAnyPermission, all - hasAllPermissions
   * @returns {boolean}
   */
  const hasAccess = (permissions, check = "any") => {
    if (Array.isArray(permissions)) {
      if (check === "any") {
        return hasAnyPermission(permissions);
      }
      return hasAllPermissions(permissions);
    } else if (typeof permissions === "string") {
      return hasPermission(permissions);
    }
    return false;
  };

  return {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasAccess,
  };
};
